import { apiClient } from "./apiClient";
import { VoteTypeEnum } from "../models/enums/VoteTypeEnum";

/**
 * Response from casting a vote
 */
export interface VoteResponse {
  id: number;
  entryId: number;
  userId: number;
  username: string;
  voteType: VoteTypeEnum;
  createdAt: string;
}

/**
 * Vote summary for an entry (counts + current user's vote status)
 */
export interface VoteSummaryResponse {
  entryId: number;
  plusCount: number;
  minusCount: number;
  favCount: number;
  currentUserRating: VoteTypeEnum | null;
  currentUserHasFav: boolean;
}

/**
 * Voter information for voters list popup
 */
export interface VoterResponse {
  userId: number;
  username: string;
  voteType: VoteTypeEnum;
  votedAt: string;
}

/**
 * Cast a vote on an entry.
 * If voting the same type again, it toggles off (removes the vote).
 * 
 * @param entryId Entry to vote on
 * @param voteType Type of vote (UP, DOWN, or FAV)
 * @returns VoteResponse if created, null if toggled off
 */
export const castVote = async (
  entryId: number,
  voteType: VoteTypeEnum
): Promise<VoteResponse | null> => {
  try {
    const response = await apiClient.post<VoteResponse>("/api/votes", {
      entryId,
      voteType,
    });
    // If response is empty object (204 No Content), vote was removed
    if (!response.id) {
      return null;
    }
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get vote summary for an entry (counts + current user's votes)
 * 
 * @param entryId Entry to get summary for
 * @returns VoteSummaryResponse with counts and user's vote status
 */
export const getVoteSummary = async (
  entryId: number
): Promise<VoteSummaryResponse> => {
  return apiClient.get<VoteSummaryResponse>(`/api/votes/entry/${entryId}/summary`);
};

/**
 * Get list of voters for a specific vote type on an entry
 * Used for showing voters popup when clicking on vote count
 * 
 * @param entryId Entry to get voters for
 * @param voteType Type of vote to filter by
 * @returns List of voters
 */
export const getVoters = async (
  entryId: number,
  voteType: VoteTypeEnum
): Promise<VoterResponse[]> => {
  return apiClient.get<VoterResponse[]>(
    `/api/votes/entry/${entryId}/voters?type=${voteType}`
  );
};